import _ from 'lodash';
import config from 'config';
import WebStoragePolyfill from '../data-providers/webstorage-polyfill';

export default [
  '$http', '$q', '$window', 'Raven',
  class ResourceService {
    constructor($http, $q, $window, Raven) {
      // Angular dependencies
      this._$http = $http;
      this._$q = $q;
      this._$window = $window;
      this._storage = new WebStoragePolyfill();
      this._Raven = Raven;

      // Default values
      this._API_BASE = `${config['API_BASE']}/api/v2`;
      this._httpConfigBase = {};
      this._releaseVersion = null;

      // OAuth
      this.oauthHandshake();
    }

    get(endpoint, httpConfig) {
      return this._makeRequest('get', endpoint, null, httpConfig);
    }

    post(endpoint, data, httpConfig) {
      return this._makeRequest('post', endpoint, data, httpConfig);
    }

    postFile(endpoint, data, httpConfig) {
      return this._makeRequest('post', endpoint, data, httpConfig, false, true);
    }

    put(endpoint, data, httpConfig) {
      return this._makeRequest('put', endpoint, data, httpConfig);
    }

    patch(endpoint, data, httpConfig) {
      return this._makeRequest('patch', endpoint, data, httpConfig);
    }

    delete(endpoint, httpConfig) {
      return this._makeRequest('delete', endpoint, null, httpConfig);
    }

    legacyGet(endpoint, httpConfig) {
      return this._makeRequest('get', endpoint, null, httpConfig, true);
    }

    legacyPost(endpoint, data, httpConfig) {
      return this._makeRequest('post', endpoint, data, httpConfig, true);
    }

    jsonp(url) {
      return this._$http.jsonp(url);
    }

    releaseVersion() {
      let deferred = this._$q.defer();
      if (this._releaseVersion === null) {
        this._makeRequest('get', '/version')
          .then((res) => {
            this._releaseVersion = res.version;
            deferred.resolve(this._releaseVersion);
          })
          .catch(() => deferred.reject());
      } else deferred.resolve(this._releaseVersion);

      return deferred.promise;
    }

    errorPromise(message) {
      (message || (message = null));

      // Log a Warning
      let title = (typeof message == 'string') ? message : 'ResourceService Error Promise';
      let context = (typeof message == 'object') ? message : null;
      this._Raven.captureMessage(title, {
        level: 'warning',
        extra: context
      });

      // Reject the Promise
      let deferred = this._$q.defer();
      deferred.reject(message);
      return deferred.promise;
    }

    emptyResolvedPromise(data) {
      let deferred = this._$q.defer();
      deferred.resolve(data)
      return deferred.promise;
    }

    emptyRejectedPromise(message) {
      let deferred = this._$q.defer();
      deferred.reject({
        data: {
          message: message
        }
      });
      return deferred.promise;
    }

    oauthHandshake() {
      if (!this.oauthDeferred) {
        this.oauthDeferred = this._$q.defer();
      }

      // Do nothing if not required.
      if (config['OAuth_Required'] === false) {
        this.oauthDeferred.resolve();
        return this.oauthDeferred.promise;
      }

      let oauth = this._retrieveOauthToken();
      if (oauth !== null && oauth.expiration > new Date()) {
        this._setOauthHeader(oauth);
        this.oauthDeferred.resolve();
      } else if (this.checkingOauth !== true) {
        this.checkingOauth = true;
        let httpConfig = {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        };

        this._$http.post(`${this._API_BASE}/token`, $.param(config['OAuth']), httpConfig)
          .then((token) => {
            this.storeOauthToken(token.data);
            this._setOauthHeader(this._retrieveOauthToken());
            this.checkingOauth = false;
            this.oauthDeferred.resolve();
          });
      }


      return this.oauthDeferred.promise;
    }

    storeOauthToken(token) {
      // Calculate the date/time of expiration.
      let expiration = (new Date()).getTime() + (token['expires_in'] * 999); // 3600 * 1000 is 1 hour in ms. 3600 * 999 will preemt the expiration by 3.6 seconds.
      // Add the new expiration to response object.
      _.extend(token, {
        expiration: expiration
      });
      // Store the entire object.
      this._storage.setSessionItem('oauth', JSON.stringify(token));
    }

    trashOauthToken() {
      this._storage.removeSessionItem('oauth');
    }

    _retrieveOauthToken() {
      let oauth = (this._storage.getSessionItem('oauth')) ? JSON.parse(this._storage.getSessionItem('oauth')) : null;

      if (oauth !== null) {
        oauth.expiration = new Date(oauth.expiration);
      }

      return oauth;
    }

    _setOauthHeader(oauth) {
      _.extend(this._httpConfigBase, {
        headers: {
          'authorization': `${oauth['token_type']} ${oauth['access_token']}`
        }
      });
    }

    _makeRequest(method, endpoint, data, httpConfig, overrideBase, isFileUpload) {
      (httpConfig || (httpConfig = {}));

      let uri = (overrideBase === true) ? endpoint : this._API_BASE + endpoint;
      let deferred = this._$q.defer();

      // When OAuth resolves, append the token to the request headers.
      this.oauthHandshake()
        .then(() => {
          this.oauthDeferred = null; //has to be set to null after the handshake otherwise all subsequent requests will automatically resolve.
          _.extend(httpConfig, this._httpConfigBase);
          if (isFileUpload) httpConfig.headers['Content-Type'] = undefined;

          // No data arg:
          if (method === 'get' || method === 'delete') {
            return this._$http[method](uri, httpConfig);
          }
          // Data arg:
          else if (method === 'post' || method === 'put' || method === 'patch') {
            return this._$http[method](uri, data, httpConfig);
          }
        })
        .then((success) => {
          var apiResponse = success['data']['data'] || success['data'] || success;
          deferred.resolve(apiResponse);
        }, (failure) => {
          deferred.reject(failure);
        })
        .catch(() => this.oauthDeferred = null); //if there is some error somewhere lets make sure to make this null anyway just to be safe.

      return deferred.promise;
    }
  }
];


// WEBPACK FOOTER //
// ./source/client/api-services/resource-service.js